import { lazy, Suspense } from "react"

import { createBrowserRouter, Navigate, Outlet } from "react-router-dom"

import { HUB_SKELETON_SECTIONS as GROUP_SETTINGS_HUB_SKELETON_SECTIONS } from "@/pages/group-settings/model/hub-skeleton-sections"
import { HUB_SKELETON_SECTIONS as MODERATION_SETTINGS_HUB_SKELETON_SECTIONS } from "@/pages/moderation-settings/model/hub-skeleton-sections"

import { EmptyDashboardSkeleton } from "@/pages/empty-dashboard/ui/empty-dashboard-skeleton"

import { RootLayout as NavigationLayout } from "@/pages/layout"

import { ErrorBoundary } from "@/shared/ui/error-boundary"
import { LazyLoader } from "@/shared/ui/lazy-loader"
import { NeuralNetworkLoader } from "@/shared/ui/loading/NeuralNetworkLoader"
import { SettingsHubSkeleton } from "@/shared/ui/settings-hub-skeleton"

import { observeUsageRoutes } from "@/shared/lib/usage-analytics"
import { AD_ONBOARDING_PATHS, CELESTIA_PRO_PATHS, FAQ_SECTION_IDS, getRouterContext } from "@/shared/router"

import { personalSubscriptionLoader } from "./personal-subscription-loader"
import {
  CaptchaVerificationRoute,
  DefaultRedirect,
  LoadingFallback,
  ProtectedRoute,
  StandaloneRouteWrapper
} from "./route-components"
import { contextReadyMiddleware, protectedMiddleware } from "./router-middleware"

// Type-safe fallback mapping for route-specific skeletons
type RouteFallbackKey =
  | "default"
  | "emptyDashboard"
  | "moderationSettings"
  | "groupSettings"
  | "userSettings"
  | "supportPro"

// Lazy load page components
const SlotMachine = lazy(() => import("@/pages/slot-machine"))
const GroupSettings = lazy(() => import("@/pages/group-settings").then(module => ({ default: module.GroupSettings })))
const AIStylePage = lazy(() =>
  import("@/pages/group-settings/pages/ai-style").then(module => ({ default: module.AIStylePage }))
)
const PermissionsPage = lazy(() =>
  import("@/pages/group-settings/pages/permissions").then(module => ({ default: module.PermissionsPage }))
)
const AIAntispamLayout = lazy(() =>
  import("@/pages/group-settings/pages/ai-antispam").then(module => ({ default: module.AIAntispamLayout }))
)
const AIAntispamPage = lazy(() =>
  import("@/pages/group-settings/pages/ai-antispam").then(module => ({ default: module.AIAntispamPage }))
)
const AIAntispamStatisticsPage = lazy(() =>
  import("@/pages/group-settings/pages/ai-antispam/pages/statistics").then(module => ({
    default: module.AIAntispamStatisticsPage
  }))
)
const Customization = lazy(() => import("@/pages/prompt-customisation"))
const Settings = lazy(() => import("@/pages/settings").then(module => ({ default: module.SettingsPage })))
const ModerationSettings = lazy(() =>
  import("@/pages/moderation-settings").then(module => ({ default: module.ModerationSettings }))
)
const EmptyDashboard = lazy(() => import("@/pages/empty-dashboard"))
const WelcomePage = lazy(() => import("@/pages/welcome").then(module => ({ default: module.WelcomePage })))
const AdGridOnboardingPage = lazy(() =>
  import("@/pages/welcome").then(module => ({ default: module.AdGridOnboardingPage }))
)
const CelestiaProPage = lazy(() => import("@/pages/celestia-pro").then(module => ({ default: module.CelestiaProPage })))
const BusinessSubscriptionPage = lazy(() =>
  import("@/pages/celestia-pro/subscription").then(module => ({ default: module.SubscriptionPage }))
)
const FaqPage = lazy(() => import("@/pages/faq").then(module => ({ default: module.FaqPage })))
const BusinessModeOnboardingPage = lazy(() =>
  import("@/pages/business-mode-onboarding").then(module => ({ default: module.BusinessModeOnboardingPage }))
)

// Loading fallback component. moderationSettings/groupSettings use the same skeleton their page
// shows once its own data loads, so cold navigation is one loading state instead of two.
const fallbackRegistry: Record<RouteFallbackKey, React.ReactNode> = {
  default: <LoadingFallback />,
  emptyDashboard: <EmptyDashboardSkeleton />,
  moderationSettings: <SettingsHubSkeleton sections={MODERATION_SETTINGS_HUB_SKELETON_SECTIONS} />,
  groupSettings: (
    <SettingsHubSkeleton
      sections={GROUP_SETTINGS_HUB_SKELETON_SECTIONS}
      withSummaryCard
    />
  ),
  userSettings: <NeuralNetworkLoader />,
  supportPro: <NeuralNetworkLoader />
}

// Helper to wrap route elements with Suspense fallback
const withRouteFallback = (element: React.ReactNode, key: RouteFallbackKey = "default") => (
  <Suspense fallback={fallbackRegistry[key]}>{element}</Suspense>
)

export const router = createBrowserRouter(
  [
    {
      path: "/welcome",
      middleware: [contextReadyMiddleware],
      element: withRouteFallback(<WelcomePage />)
    },
    {
      path: AD_ONBOARDING_PATHS.grid,
      middleware: [contextReadyMiddleware],
      element: withRouteFallback(<AdGridOnboardingPage />)
    },
    {
      path: "/",
      middleware: [contextReadyMiddleware],
      element: (
        <StandaloneRouteWrapper>
          <NavigationLayout />
        </StandaloneRouteWrapper>
      ),
      children: [
        {
          index: true,
          element: <DefaultRedirect />
        },
        {
          path: "dashboard",
          element: withRouteFallback(<EmptyDashboard />, "emptyDashboard")
        },
        {
          path: "group-settings",
          element: (
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            {
              index: true,
              element: withRouteFallback(<GroupSettings />, "groupSettings")
            },
            {
              path: "ai-style",
              element: withRouteFallback(<AIStylePage />, "groupSettings")
            },
            {
              path: "permissions",
              element: withRouteFallback(<PermissionsPage />, "groupSettings")
            },
            {
              path: "ai-antispam",
              element: withRouteFallback(<AIAntispamLayout />, "groupSettings"),
              children: [
                {
                  index: true,
                  element: <AIAntispamPage />
                },
                {
                  path: "statistics",
                  element: <AIAntispamStatisticsPage />
                }
              ]
            }
          ]
        },
        {
          path: "user-profile",
          element: <Outlet />,
          children: [
            {
              index: true,
              lazy: async () => {
                const { UserProfilePage } = await import("@/pages/user-profile")
                return { Component: UserProfilePage, HydrateFallback: NeuralNetworkLoader }
              }
            },
            {
              path: "statistics",
              lazy: async () => {
                const { StatisticsPage } = await import("@/pages/user-profile/pages/statistics")
                return { Component: StatisticsPage, HydrateFallback: NeuralNetworkLoader }
              }
            },
            {
              path: "ai-provider",
              lazy: async () => {
                const { AIProviderPage } = await import("@/pages/user-profile/pages/ai-provider")
                return { Component: AIProviderPage, HydrateFallback: NeuralNetworkLoader }
              }
            },
            {
              path: "ai-mode",
              lazy: async () => {
                const { AIModeSelectPage } = await import("@/pages/user-profile/pages/ai-mode")
                return { Component: AIModeSelectPage, HydrateFallback: NeuralNetworkLoader }
              }
            },
            {
              path: "personal-subscription",
              lazy: async () => {
                const { PersonalSubscriptionPage } = await import("@/pages/user-profile/pages/personal-subscription")
                return {
                  Component: PersonalSubscriptionPage,
                  HydrateFallback: NeuralNetworkLoader,
                  loader: personalSubscriptionLoader
                }
              }
            },
            {
              path: "finance",
              lazy: async () => {
                const { FinancePage } = await import("@/pages/user-profile/pages/finance")
                return { Component: FinancePage, HydrateFallback: NeuralNetworkLoader }
              }
            },
            {
              path: "finance/subscription",
              lazy: async () => {
                const { SubscriptionPage } = await import("@/pages/user-profile/pages/subscription")
                return { Component: SubscriptionPage, HydrateFallback: NeuralNetworkLoader }
              }
            },
            {
              path: "balance/distribute",
              lazy: async () => {
                const { BalanceDistributePage } = await import("@/pages/user-profile/pages/balance-distribute")
                return { Component: BalanceDistributePage, HydrateFallback: NeuralNetworkLoader }
              }
            },
            {
              path: "api-key",
              lazy: async () => {
                const { ApiKeyPage } = await import("@/pages/user-profile/pages/api-key")
                return { Component: ApiKeyPage, HydrateFallback: NeuralNetworkLoader }
              }
            },
            {
              path: "card-customization/:mode",
              lazy: async () => {
                const { CardCustomizationPage } = await import("@/pages/user-profile/pages/card-customization")
                return { Component: CardCustomizationPage, HydrateFallback: NeuralNetworkLoader }
              }
            }
          ]
        },
        {
          path: "settings",
          element: withRouteFallback(
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          )
        },
        {
          path: "customization",
          element: withRouteFallback(<Customization />)
        },
        {
          path: "moderation",
          element: withRouteFallback(<ModerationSettings />, "moderationSettings")
        },
        {
          path: "celestia-pro",
          element: withRouteFallback(
            <ProtectedRoute allowMissingChat>
              <CelestiaProPage />
            </ProtectedRoute>,
            "supportPro"
          )
        },
        {
          path: "celestia-pro/subscription",
          element: withRouteFallback(
            <ProtectedRoute allowMissingChat>
              <BusinessSubscriptionPage />
            </ProtectedRoute>,
            "supportPro"
          )
        },
        {
          path: "faq",
          children: [
            { index: true, element: withRouteFallback(<FaqPage />) },
            ...FAQ_SECTION_IDS.map(section => ({
              path: section,
              element: withRouteFallback(<FaqPage section={section} />)
            }))
          ]
        },

        {
          path: "support-pro",
          element: (
            <Navigate
              to="/celestia-pro"
              replace
            />
          )
        },
        {
          path: "business-mode",
          element: (
            <Navigate
              to={CELESTIA_PRO_PATHS.businessMode}
              replace
            />
          )
        },
        {
          path: "*",
          element: (
            <Navigate
              to="/dashboard"
              replace
            />
          )
        }
      ]
    },

    {
      path: "/captcha-verification",
      element: <CaptchaVerificationRoute />
    },

    {
      path: "/casino",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <SlotMachine />
        </Suspense>
      )
    },

    {
      path: "/chat-grids",
      element: (
        <StandaloneRouteWrapper>
          <Outlet />
        </StandaloneRouteWrapper>
      ),
      children: [
        {
          index: true,
          lazy: async () => {
            const { ChatGridsPage } = await import("@/pages/chat-grids")
            return { Component: ChatGridsPage, HydrateFallback: LoadingFallback }
          }
        },
        {
          path: "create",
          lazy: async () => {
            const { CreateChatGridPage } = await import("@/pages/chat-grids")
            return { Component: CreateChatGridPage, HydrateFallback: LoadingFallback }
          }
        },
        {
          path: ":gridId",
          lazy: async () => {
            const { ChatGridPage } = await import("@/pages/chat-grid")
            return { Component: ChatGridPage, HydrateFallback: LoadingFallback }
          }
        },
        {
          path: ":gridId/chats",
          lazy: async () => {
            const { ChatGridChatsPage } = await import("@/pages/chat-grid")
            return { Component: ChatGridChatsPage, HydrateFallback: LoadingFallback }
          }
        },
        {
          path: ":gridId/chats/add",
          lazy: async () => {
            const { AddChatGridChatsPage } = await import("@/pages/chat-grid")
            return { Component: AddChatGridChatsPage, HydrateFallback: LoadingFallback }
          }
        },
        {
          path: ":gridId/chats/:chatId",
          lazy: async () => {
            const { ChatGridChatPage } = await import("@/pages/chat-grid")
            return { Component: ChatGridChatPage, HydrateFallback: LoadingFallback }
          }
        },
        {
          path: ":gridId/sections/:sectionKey",
          lazy: async () => {
            const { ChatGridSectionPage } = await import("@/pages/chat-grid")
            return { Component: ChatGridSectionPage, HydrateFallback: LoadingFallback }
          }
        },
        {
          path: ":gridId/journal",
          lazy: async () => {
            const { ChatGridJournalPage } = await import("@/pages/chat-grid")
            return { Component: ChatGridJournalPage, HydrateFallback: LoadingFallback }
          }
        }
      ]
    },

    {
      path: "/chat-grids/:gridId/admins",
      element: (
        <StandaloneRouteWrapper>
          <Outlet />
        </StandaloneRouteWrapper>
      ),
      children: [
        {
          index: true,
          lazy: async () => {
            const { ChatGridAdminsPage } = await import("@/pages/chat-grid-admins")
            return { Component: ChatGridAdminsPage, HydrateFallback: LoadingFallback }
          }
        },
        {
          path: "add",
          lazy: async () => {
            const { ChatGridAddAdminsPage } = await import("@/pages/chat-grid-admins")
            return { Component: ChatGridAddAdminsPage, HydrateFallback: LoadingFallback }
          }
        }
      ]
    },

    {
      path: CELESTIA_PRO_PATHS.supportMode,
      middleware: [contextReadyMiddleware],
      lazy: async () => {
        const { SupportModePage } = await import("@/pages/support-pro")
        return { Component: SupportModePage, HydrateFallback: LoadingFallback }
      }
    },
    {
      path: CELESTIA_PRO_PATHS.businessMode,
      middleware: [contextReadyMiddleware],
      lazy: async () => {
        const { BusinessModePage } = await import("@/pages/business-mode-settings")
        return { Component: BusinessModePage, HydrateFallback: LoadingFallback }
      }
    },
    {
      path: "/celestia-pro/business-mode/ai-provider",
      lazy: async () => {
        const { BusinessAIProviderPage } = await import("@/pages/business-mode-settings/pages/ai-provider")
        return { Component: BusinessAIProviderPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/celestia-pro/business-mode/onboarding",
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <BusinessModeOnboardingPage />
        </Suspense>
      )
    },
    {
      path: AD_ONBOARDING_PATHS.business,
      middleware: [contextReadyMiddleware],
      lazy: async () => {
        const { BusinessModeAdOnboardingPage } = await import("@/pages/business-mode-onboarding")
        return { Component: BusinessModeAdOnboardingPage, HydrateFallback: LoadingFallback }
      }
    },

    {
      path: CELESTIA_PRO_PATHS.clones,
      children: [
        {
          index: true,
          lazy: async () => {
            const { ClonesPage } = await import("@/pages/celestia-pro/clones")
            return { Component: ClonesPage, HydrateFallback: LoadingFallback }
          }
        },
        {
          path: "create",
          lazy: async () => {
            const { CreateClonePage } = await import("@/pages/celestia-pro/clones/pages/create")
            return {
              element: withRouteFallback(
                <ErrorBoundary>
                  <CreateClonePage />
                </ErrorBoundary>
              )
            }
          }
        },
        {
          path: ":botId",
          element: <Outlet />,
          children: [
            {
              index: true,
              lazy: () =>
                Promise.resolve({
                  Component: lazy(() =>
                    import("@/pages/celestia-pro/clones/pages/[botId]").then(module => ({
                      default: module.CloneSettingsPage
                    }))
                  ),
                  HydrateFallback: NeuralNetworkLoader
                })
            },
            {
              path: "chats",
              lazy: () =>
                Promise.resolve({
                  Component: lazy(() =>
                    import("@/pages/celestia-pro/clones/pages/[botId]/pages/chats").then(module => ({
                      default: module.CloneChatsPage
                    }))
                  ),
                  HydrateFallback: NeuralNetworkLoader
                })
            }
          ]
        }
      ]
    },

    {
      path: "/moderation/level-system",
      middleware: [protectedMiddleware],
      element: <Outlet />,
      children: [
        {
          index: true,
          lazy: () =>
            Promise.resolve({
              element: <LazyLoader />,
              loader: () => ({
                component: import("@/pages/level-system").then(module => module.LevelSystemPage)
              })
            })
        },
        {
          path: "notifications",
          lazy: async () => {
            const { LevelSystemNotificationsPage } = await import("@/pages/level-system/pages/notifications")
            return { Component: LevelSystemNotificationsPage, HydrateFallback: NeuralNetworkLoader }
          }
        },
        {
          path: "restrictions",
          lazy: async () => {
            const { LevelSystemRestrictionsPage } = await import("@/pages/level-system/pages/restrictions")
            return { Component: LevelSystemRestrictionsPage, HydrateFallback: NeuralNetworkLoader }
          }
        },
        {
          path: "titles",
          lazy: async () => {
            const { LevelSystemTitlesPage } = await import("@/pages/level-system/pages/titles")
            return { Component: LevelSystemTitlesPage, HydrateFallback: NeuralNetworkLoader }
          }
        },
        {
          path: "leaderboard",
          lazy: async () => {
            const { LevelSystemLeaderboardPage } = await import("@/pages/level-system/pages/leaderboard")
            return { Component: LevelSystemLeaderboardPage, HydrateFallback: NeuralNetworkLoader }
          }
        }
      ]
    },
    {
      path: "/moderation/service-messages",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { ServiceMessagesPage } = await import("@/pages/moderation-settings/service-messages")
        return { Component: ServiceMessagesPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/invitation-settings",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { InvitationSettingsPage } = await import("@/pages/moderation-settings/invitation-settings")
        return { Component: InvitationSettingsPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/service-messages/commands/edit",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { ServiceMessagesCommandsEditPage } = await import(
          "@/pages/moderation-settings/service-messages/pages/commands-edit"
        )
        return { Component: ServiceMessagesCommandsEditPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/commands",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { CommandsPage } = await import("@/pages/moderation-settings/commands")
        return { Component: CommandsPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/reports",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { ReportsPage } = await import("@/pages/moderation-reports")
        return { Component: ReportsPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/reports/:reportId",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { ReportDecisionPage } = await import("@/pages/moderation-reports")
        return { Component: ReportDecisionPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/blacklist",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { BlacklistPage } = await import("@/pages/moderation-settings/blacklist")
        return { Component: BlacklistPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/blacklist/create",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { BlacklistCreatePage } = await import("@/pages/moderation-settings/blacklist/pages/create")
        return { Component: BlacklistCreatePage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/blacklist/:id",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { BlacklistEditPage } = await import("@/pages/moderation-settings/blacklist/pages/[id]")
        return { Component: BlacklistEditPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/commands/:slug",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { CommandSettingsPage } = await import("@/pages/moderation-settings/commands/pages/[slug]")
        return { Component: CommandSettingsPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/moderation/commands/edit",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { CommandsEditPage } = await import("@/pages/moderation-settings/commands/pages/edit")
        return { Component: CommandsEditPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/ai-learning",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { AILearningPage } = await import("@/pages/group-settings/pages/ai-learning")
        return { Component: AILearningPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/ai-learning/chat-history",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { AILearningChatHistoryPage } = await import(
          "@/pages/group-settings/pages/ai-learning/pages/chat-history"
        )
        return { Component: AILearningChatHistoryPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/ai-learning/chat-history/topics",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { AILearningChatHistoryTopicsPage } = await import(
          "@/pages/group-settings/pages/ai-learning/pages/chat-history/pages/topics"
        )
        return { Component: AILearningChatHistoryTopicsPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/ai-learning/support",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { AILearningSupportPage } = await import("@/pages/group-settings/pages/ai-learning/pages/support")
        return { Component: AILearningSupportPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/ai-learning/support/trainers",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { AILearningSupportTrainersPage } = await import(
          "@/pages/group-settings/pages/ai-learning/pages/support/pages/trainers"
        )
        return { Component: AILearningSupportTrainersPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/ai-learning/support/topics",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { AILearningSupportTopicsPage } = await import(
          "@/pages/group-settings/pages/ai-learning/pages/support/pages/topics"
        )
        return { Component: AILearningSupportTopicsPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/ai-learning/sleep-mode",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { AILearningSleepModePage } = await import("@/pages/group-settings/pages/ai-learning/pages/sleep-mode")
        return { Component: AILearningSleepModePage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/ai-learning/proposals",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { AILearningProposalsPage } = await import("@/pages/group-settings/pages/ai-learning/pages/proposals")
        return { Component: AILearningProposalsPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/ai-learning/proposals/:id",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { AILearningProposalDetailPage } = await import(
          "@/pages/group-settings/pages/ai-learning/pages/proposals/[id]"
        )
        return { Component: AILearningProposalDetailPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    {
      path: "/group-settings/event-log",
      middleware: [protectedMiddleware],
      lazy: async () => {
        const { EventLogPage } = await import("@/pages/group-settings/pages/event-log")
        return { Component: EventLogPage, HydrateFallback: NeuralNetworkLoader }
      }
    },
    // Vite folds import.meta.env.DEV to false in production builds, so the route and its chunk are dropped.
    ...(import.meta.env.DEV
      ? [
          {
            path: "/dev/xp-prototype",
            lazy: async () => {
              const { XpPrototypePage } = await import("@/pages/xp-prototype")
              return { Component: XpPrototypePage, HydrateFallback: NeuralNetworkLoader }
            }
          }
        ]
      : [])
  ],
  {
    getContext: getRouterContext
  }
)

observeUsageRoutes(router)
